import { useMemo } from 'react';
import type { JSX } from 'react';

import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { frFR as i18nGrid } from '@mui/x-data-grid/locales';
import { frFR as i18nPickers } from '@mui/x-date-pickers/locales';
import { frFR as i18nCore } from '@mui/material/locale';

import { muiColors } from '@e-socna/colors';
import { PaletteModeEnum } from '@e-socna/data-layer';

import { useUiSettingsContext } from '../UiSettingsContext/hooks/useUiSettingsContext.hook';

const { secondary, error, warning, info, success } = muiColors;

interface Props {
  readonly children: JSX.Element | readonly JSX.Element[];
  readonly fixedPalette?: PaletteModeEnum;
}

export const ThemeContextProvider = ({
  children,
  fixedPalette,
}: Readonly<Props>) => {
  const { paletteMode, mainColor } = useUiSettingsContext();

  const paletteModeToUse = fixedPalette ?? paletteMode;

  const theme = useMemo(
    () =>
      createTheme(
        {
          palette: {
            mode: paletteModeToUse === PaletteModeEnum.Dark ? 'dark' : 'light',
            primary: {
              main: mainColor,
            },
            secondary: {
              main: secondary,
            },
            error: {
              main: error,
            },
            warning: {
              main: warning,
            },
            info: {
              main: info,
            },
            success: {
              main: success,
            },
          },
          components: {
            MuiTablePagination: {
              styleOverrides: {
                root: {
                  marginRight: 48,
                },
              },
            },
          },
        },
        i18nGrid,
        i18nPickers,
        i18nCore,
      ),
    [mainColor, paletteModeToUse],
  );

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
};
